import { TableInstance, Row } from 'react-table';
import NoData from './NoData';
import TablePagination from './TablePagination';
import SortClass from './SortClass';

interface ReactTableProps<T extends object> {
    // React-table instance props
    getTableProps: TableInstance<T>['getTableProps'];
    getTableBodyProps: TableInstance<T>['getTableBodyProps'];
    headerGroups: TableInstance<T>['headerGroups'];
    prepareRow: TableInstance<T>['prepareRow'];
    page: Row<T>[];

    // Pagination props
    canPreviousPage: boolean;
    canNextPage: boolean;
    pageOptions: number[];
    pageCount: number;
    gotoPage: (pageIndex: number) => void;
    nextPage: () => void;
    previousPage: () => void;
    setPageSize: (pageSize: number) => void;
    pageIndex: number;
    pageSize: number;

    // Data props
    dataLength: number;

    // NoData props
    error?: string | null;
    loading?: boolean;

    // Optional custom props
    getRowProps?: (row: Row<T>) => { className?: string } & Record<string, unknown>;
    renderCell?: (cell: import('react-table').Cell<T>) => React.ReactNode;
}

export default function ReactTable<T extends object>(props: ReactTableProps<T>) {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        pageIndex,
        pageSize,
        dataLength,
        error = null,
        loading = false,
        getRowProps,
        renderCell,
    } = props;

    const renderSortIcon = (column: any) => {
        return <SortClass column={column}/>;

    };

    const getHeaderClassName = (column: any) => {
        return (column as any).headerClassName || (column as any).className
    };

    return (
            <div className='table-responsive'>
                <table {...getTableProps()} className="table">
                    <thead>
                    {headerGroups.map((headerGroup, index) => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                                {headerGroup.headers.map((column, colIndex) => (
                                        <th
                                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                                className={getHeaderClassName(column)}
                                                key={colIndex}
                                        >
                                            {column.render('Header')}
                                            {renderSortIcon(column)}
                                        </th>
                                ))}
                            </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {page.map((row, rowIndex) => {
                        prepareRow(row);
                        const rowProps = row.getRowProps();
                        const customRowProps = getRowProps?.(row) || {};
                        return (
                                <tr {...rowProps} {...customRowProps} key={rowIndex}>
                                    {row.cells.map((cell, cellIndex) => {
                                        return (
                                                <td
                                                        {...cell.getCellProps()}
                                                        className={(cell.column as any).className}
                                                        style={{ borderLeft: (cell.column as any).color }}
                                                        key={cellIndex}
                                                >
                                                    {renderCell ? renderCell(cell) : cell.render('Cell')}
                                                </td>
                                        );
                                    })}
                                </tr>
                        );
                    })}
                    </tbody>
                </table>
                <NoData
                        error={error}
                        loading={loading}
                        length={dataLength}
                />
                <TablePagination
                        length={dataLength}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        pageOptions={pageOptions}
                        pageCount={pageCount}
                        canPreviousPage={canPreviousPage}
                        previousPage={previousPage}
                        canNextPage={canNextPage}
                        nextPage={nextPage}
                        gotoPage={gotoPage}
                        pageIndex={pageIndex}
                />
            </div>
    );
}
